import { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PlatformIcon, getPlatformLabel } from "@/components/ui/platform-icon";
import { apiFetch } from "@/lib/api-client";
import { formatDate, useLocale } from "@/i18n/format";
import { toast } from "sonner";
import { AlertTriangle, Calendar, Loader2 } from "lucide-react";

export interface SchedulePreviewSlot {
  pieceId: string;
  shortCode: string;
  platform: string;
  suggestedTime: string;
  score: number;
  reasoning: string;
}

export interface SchedulePreviewUnplaced {
  pieceId: string;
  shortCode: string;
  platform: string;
  reason: "no_slot" | "no_active_account";
}

interface ScheduleAllDialogProps {
  brandId: string;
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * "Schedule all approved": previews timing-engine slots for the project's
 * unscheduled APPROVED pieces (read-only server endpoint), then executes by
 * looping the existing per-piece schedule endpoint sequentially so the
 * Zernio write path stays single. Per-piece failures are surfaced.
 */
export function ScheduleAllDialog({
  brandId,
  projectId,
  open,
  onOpenChange,
}: ScheduleAllDialogProps) {
  const { t } = useTranslation("projects");
  const locale = useLocale();
  const qc = useQueryClient();
  const [scheduling, setScheduling] = useState(false);
  const [progress, setProgress] = useState(0);
  const confirmInFlight = useRef(false);

  const preview = useQuery({
    queryKey: ["schedulePreview", brandId, projectId],
    queryFn: () =>
      apiFetch<{ slots: SchedulePreviewSlot[]; unplaced: SchedulePreviewUnplaced[] }>(
        `/brands/${brandId}/projects/${projectId}/schedule-preview`,
      ),
    enabled: open,
    // Always preview against the live calendar when the dialog opens.
    staleTime: 0,
    refetchOnMount: "always",
  });

  const slots = preview.data?.slots ?? [];
  const unplaced = preview.data?.unplaced ?? [];

  async function handleConfirm() {
    if (confirmInFlight.current) return;
    confirmInFlight.current = true;
    setScheduling(true);
    setProgress(0);
    const failures: string[] = [];

    for (let i = 0; i < slots.length; i++) {
      const slot = slots[i];
      setProgress(i + 1);
      try {
        await apiFetch(`/brands/${brandId}/pieces/${slot.pieceId}/schedule`, {
          method: "POST",
          body: JSON.stringify({
            platforms: [{ platform: slot.platform, scheduledFor: slot.suggestedTime }],
          }),
        });
      } catch {
        failures.push(slot.shortCode);
      }
    }

    qc.invalidateQueries({ queryKey: ["project", brandId, projectId] });
    qc.invalidateQueries({ queryKey: ["projectPieces", brandId] });
    qc.invalidateQueries({ queryKey: ["brandPieces", brandId] });
    qc.invalidateQueries({ queryKey: ["pieceByCode", brandId] });
    qc.invalidateQueries({ queryKey: ["calendar", brandId] });
    qc.invalidateQueries({ queryKey: ["unscheduled", brandId] });
    qc.invalidateQueries({ queryKey: ["schedulePreview", brandId, projectId] });

    const scheduled = slots.length - failures.length;
    if (failures.length === 0) {
      toast.success(t("projects:scheduleDialog.toast.success", { count: scheduled }));
    } else {
      toast.warning(
        t("projects:scheduleDialog.toast.partial", { scheduled, total: slots.length, failed: failures.join(", ") }),
      );
    }

    setScheduling(false);
    confirmInFlight.current = false;
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !scheduling && onOpenChange(o)}>
      <DialogContent className="max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("projects:scheduleDialog.title")}</DialogTitle>
          <DialogDescription>
            {t("projects:scheduleDialog.description")}
          </DialogDescription>
        </DialogHeader>

        {preview.isLoading ? (
          <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
            <Loader2 size={14} className="animate-spin" /> {t("projects:scheduleDialog.findingSlots")}
          </div>
        ) : preview.isError ? (
          <p className="py-4 text-sm text-red-400">{t("projects:scheduleDialog.previewFailed")}</p>
        ) : (
          <div className="space-y-3">
            {slots.length === 0 && unplaced.length === 0 && (
              <p className="text-sm text-muted-foreground">
                {t("projects:scheduleDialog.nothingToSchedule")}
              </p>
            )}

            {slots.length > 0 && (
              <ul className="space-y-2">
                {slots.map((slot) => (
                  <li key={slot.pieceId} className="rounded-md border p-2 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs">{slot.shortCode}</span>
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <PlatformIcon platform={slot.platform} size={12} />
                        {getPlatformLabel(slot.platform)}
                      </span>
                      <span className="ml-auto text-xs font-medium">
                        {formatDate(slot.suggestedTime, locale, {
                          weekday: "short",
                          month: "short",
                          day: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                        })}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">{slot.reasoning}</p>
                  </li>
                ))}
              </ul>
            )}

            {unplaced.length > 0 && (
              <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-2">
                <p className="flex items-center gap-1.5 text-xs font-medium text-amber-400">
                  <AlertTriangle size={12} /> {t("projects:scheduleDialog.unplacedWarning", { count: unplaced.length })}
                </p>
                <ul className="mt-1 space-y-0.5">
                  {unplaced.map((u) => (
                    <li key={u.pieceId} className="text-xs text-muted-foreground">
                      <span className="font-mono">{u.shortCode}</span> ({getPlatformLabel(u.platform)})
                      {" — "}
                      {t(`projects:scheduleDialog.unplacedReason.${u.reason}`)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={scheduling}>
            {t("common:actions.cancel")}
          </Button>
          <Button onClick={handleConfirm} disabled={scheduling || slots.length === 0}>
            <Calendar size={14} className="mr-2" />
            {scheduling
              ? t("projects:scheduleDialog.schedulingProgress", { progress, total: slots.length })
              : t("projects:scheduleDialog.scheduleButton", { count: slots.length })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
